import type { Href } from "expo-router";
import { Modal, Pressable, ScrollView, StyleSheet, View } from "react-native";

import { useTranslation } from "@luna-restaurant/i18n";
import {
  Button,
  Icon,
  ThemedLoader,
  ThemedText,
  useCustomTheme,
} from "@luna-restaurant/ui";

export type SchoolNavigationItem = {
  key: string;
  label: string;
  href: Href;
  isActive?: boolean;
};

type SchoolBurgerMenuProps = {
  isOpen: boolean;
  navigationItems: readonly SchoolNavigationItem[];
  currentSchool?: {
    name: string;
    abbreviation?: string;
  } | null;
  currentSchoolLoading?: boolean;
  showSchoolInPanel?: boolean;
  logoutLabel: string;
  onClose: () => void;
  onNavigate: (href: Href) => void;
  onLogout: () => void;
};

export const SchoolBurgerMenu = ({
  isOpen,
  navigationItems,
  currentSchool,
  currentSchoolLoading,
  showSchoolInPanel = false,
  logoutLabel,
  onClose,
  onNavigate,
  onLogout,
}: SchoolBurgerMenuProps) => {
  const { colors, fonts } = useCustomTheme();
  const { t } = useTranslation();

  return (
    <Modal
      visible={isOpen}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <Pressable
          style={styles.backdrop}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel={t("header.closeMenu")}
        />

        <View style={[styles.menuCard, { backgroundColor: colors.background }]}>
          <ScrollView contentContainerStyle={styles.content}>
            {showSchoolInPanel && (currentSchool || currentSchoolLoading) && (
              <View style={styles.schoolPanel}>
                <Icon
                  iconSet="material-community"
                  icon="school-outline"
                  size={22}
                  color={colors.border}
                  cssStyle={styles.schoolIcon}
                />
                {currentSchool ? (
                  <ThemedText
                    numberOfLines={3}
                    ellipsizeMode="tail"
                    style={[
                      styles.schoolText,
                      fonts.semiBold,
                      { color: colors.semiMuted },
                    ]}
                  >
                    {currentSchool.name}
                    {currentSchool.abbreviation &&
                      ` (${currentSchool.abbreviation})`}
                  </ThemedText>
                ) : (
                  <ThemedLoader spinnerSize={22} />
                )}
              </View>
            )}

            <View style={styles.items}>
              {navigationItems.map((item) => (
                <Pressable
                  key={item.key}
                  testID={`menu-item-${item.key}`}
                  accessibilityRole="button"
                  onPress={() => {
                    onClose();
                    onNavigate(item.href);
                  }}
                  style={styles.itemRow}
                >
                  <ThemedText
                    style={[
                      styles.itemLabel,
                      item.isActive ? fonts.semiBold : fonts.regular,
                      { color: item.isActive ? colors.border : colors.text },
                    ]}
                  >
                    {item.label}
                  </ThemedText>
                  <Icon
                    iconSet="material-community"
                    icon="chevron-right"
                    size={24}
                    color={colors.text}
                  />
                </Pressable>
              ))}
            </View>

            <Button
              buttonText={logoutLabel}
              onPress={() => {
                onClose();
                onLogout();
              }}
              iconName="logout"
              iconPosition="left"
              iconSize={24}
              iconColor={colors.background}
              buttonStyle={[
                styles.actionButton,
                { backgroundColor: colors.palette.spinach30 },
              ]}
              buttonTextStyle={[
                styles.buttonText,
                fonts.bold,
                { color: colors.background },
              ]}
            />
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.28)",
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  menuCard: {
    marginTop: 84,
    marginRight: 24,
    marginLeft: "auto",
    width: 380,
    maxWidth: "92%",
    borderRadius: 24,
    boxShadow: "0px 20px 44px 0px #00000033",
    elevation: 8,
    zIndex: 10,
  },
  content: {
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 24,
    gap: 24,
  },
  schoolPanel: {
    flexDirection: "row",
    alignItems: "center",
    paddingTop: 8,
  },
  schoolIcon: {
    marginRight: 8,
  },
  schoolText: {
    flex: 1,
    fontSize: 12,
    lineHeight: 16,
    letterSpacing: 12 * 0.02,
  },
  items: {
    gap: 8,
  },
  itemRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 14,
  },
  itemLabel: {
    fontSize: 20,
    lineHeight: 26,
  },
  actionButton: {
    height: 44,
    borderRadius: 22,
    justifyContent: "center",
    gap: 4,
  },
  buttonText: {
    flexGrow: 0,
    fontSize: 16,
    lineHeight: 21,
    paddingHorizontal: 8,
  },
});
